import axios from 'axios';
import { normalizeAxiosError } from './errors.js';
const PUBLIC_AUTH_PATHS = [
    '/api/v1/auth/login',
    '/api/v1/auth/refresh',
    '/api/v1/auth/super-admin-login',
    '/api/v1/auth/register-org',
    '/api/v1/auth/forgot-password',
    '/api/v1/auth/reset-password',
    '/api/v1/auth/invitations/accept',
];
function isPublicAuthPath(url) {
    if (!url)
        return false;
    return PUBLIC_AUTH_PATHS.some((p) => url.includes(p));
}
export function createApiClient(opts) {
    const client = axios.create({
        baseURL: opts.baseUrl,
        timeout: 30_000,
        headers: { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
    });
    // Refresh queue: when a refresh is in flight, hold all incoming 401-retryable
    // requests until it completes — then release them with the new token (or
    // the failure mode so each waiter handles it the same way as the leader).
    let refreshing = null;
    const waiters = [];
    function waitForRefresh() {
        return new Promise((resolve) => waiters.push(resolve));
    }
    async function performRefresh() {
        const refresh = await opts.getRefreshToken();
        if (!refresh)
            return 'auth-failed';
        const orgSlug = await opts.getOrgSlug();
        if (!orgSlug)
            return 'auth-failed';
        try {
            const { data } = await axios.post(`${opts.baseUrl}/api/v1/auth/refresh`, { refresh_token: refresh, org_slug: orgSlug }, { headers: { 'Content-Type': 'application/json' } });
            await opts.onTokenRefresh(data.access_token, data.refresh_token);
            return data.access_token;
        }
        catch (err) {
            // Only sign out when the server explicitly rejects the refresh token.
            // Network errors, 5xx, timeouts → keep the user signed in and let
            // them retry the action. Losing connection should not log you out.
            const status = err?.response?.status;
            if (status === 401 || status === 403)
                return 'auth-failed';
            return 'transient';
        }
    }
    async function handleOutcome(outcome, original, originalErr) {
        if (outcome === 'auth-failed') {
            await opts.onAuthError();
            return Promise.reject(normalizeAxiosError(originalErr));
        }
        if (outcome === 'transient') {
            // Don't sign out — bubble the original 401 so the caller can decide
            // whether to retry. The next user action will trigger a fresh refresh.
            return Promise.reject(normalizeAxiosError(originalErr));
        }
        // outcome is the new access token
        original.headers.set('Authorization', `Bearer ${outcome}`);
        return client(original);
    }
    client.interceptors.request.use(async (config) => {
        const token = await opts.getAccessToken();
        if (token && !isPublicAuthPath(config.url)) {
            config.headers.set('Authorization', `Bearer ${token}`);
        }
        // Attach the active customer context. "all" / null = consolidated
        // view — backend returns rows for every customer the user can see.
        // We send the header even on auth paths because it's harmless; the
        // backend ignores it pre-auth.
        if (opts.getCustomerId && !isPublicAuthPath(config.url)) {
            const customerId = opts.getCustomerId();
            if (customerId)
                config.headers.set('X-Customer-Id', customerId);
        }
        if (opts.getGroupScope && !isPublicAuthPath(config.url)) {
            const scope = opts.getGroupScope();
            if (scope) {
                config.headers.set('X-Scope-Kind', scope.kind);
                config.headers.set('X-Scope-Id', scope.id);
            }
        }
        return config;
    });
    client.interceptors.response.use((res) => res, async (err) => {
        const original = err.config;
        const status = err.response?.status;
        if (status !== 401 || !original || original._retry || isPublicAuthPath(original.url)) {
            return Promise.reject(normalizeAxiosError(err));
        }
        original._retry = true;
        // If a refresh is already running, queue this request behind it.
        if (refreshing) {
            const outcome = await waitForRefresh();
            return handleOutcome(outcome, original, err);
        }
        refreshing = performRefresh();
        const outcome = await refreshing;
        refreshing = null;
        // Release queued requests with the same outcome.
        waiters.splice(0).forEach((w) => w(outcome));
        return handleOutcome(outcome, original, err);
    });
    return client;
}
//# sourceMappingURL=client.js.map